import React from 'react';
import _merge from 'lodash/merge';
import { getContext } from 'common';

import RippleEffect, { RippleEffectProps } from '../RippleEffect';
import Styled from './styles';

export interface CalendarDayProps extends React.ComponentPropsWithRef<'div'> {
  /** Set to `true`, if the day is in range. */
  inRange?: boolean;
  /** Set to `true`, if the day is first in range. */
  firstInRange?: boolean;
  /** Set to `true`, if the day is last in range. */
  lastInRange?: boolean;
  /** The day label. */
  day: React.ReactNode;
  /** If `true`, the button will be disabled. */
  disabled?: boolean;
  /** If `true`, the button will be rendered in selected style. */
  selected?: boolean;
  /** If `true`, the button will be rendered in error style. */
  error?: boolean;
  /** Ripple style. */
  rippleStyle?: Partial<RippleEffectProps>;
  /** To alter the button DOM element attributes. */
  buttonProps?: React.ComponentProps<'button'>;
  /** To override the theme please refer to the "Readme" tab to learn more. */
  theme?: any;
}

/**
 * <CalendarDay />, any other props will be provided to the root `button` element.
 * To get the ref of the outermost element use `ref` prop. [Read React useRef hook documentation](https://reactjs.org/docs/hooks-reference.
 */
const CalendarDay = React.forwardRef<HTMLElement, CalendarDayProps>((props: CalendarDayProps, ref) => getContext(({ RTL, theme }) => {
  const {
    day,
    rippleStyle,
    disabled,
    selected,
    error,
    theme: propTheme,
    buttonProps,
    ...otherProps
  } = props;

  const resolvedTheme = _merge({}, theme.CALENDAR.DAY, propTheme);
  return (
    <Styled.CalendarDay
      {...otherProps}
      $theme={resolvedTheme}
      $rtl={RTL}
    >
      <RippleEffect
        disabled={disabled}
        rippleStyle={{
          borderRadius: '100%',
          backgroundColor: resolvedTheme.rippleColor,
        }}
        style={{
          flex: '0',
          borderRadius: '100%',
          width: 'min-content',
          ...rippleStyle
        }}
      >
        <Styled.CalendarDayButton
          ref={ref}
          $rtl={RTL}
          $theme={resolvedTheme}
          disabled={disabled}
          selected={selected}
          error={error}
          {...buttonProps}
        >
          {day}
        </Styled.CalendarDayButton>
      </RippleEffect>
    </Styled.CalendarDay>
  );
}));

CalendarDay.displayName = 'CalendarDay';

CalendarDay.defaultProps = {
  inRange: false,
  disabled: false,
  firstInRange: false,
  lastInRange: false,
  selected: false,
  error: false,
  rippleStyle: {},
  buttonProps: {},
  theme: {}
};

export default CalendarDay;
